import React from 'react';
import { Box, Tabs, Tab, IconButton, Menu, MenuItem, AppBar, Toolbar, Typography } from '@mui/material';
import { 
  MenuBook as MenuBookIcon, 
  FormatQuote as FormatQuoteIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Bookmark as BookmarkIcon,
  AutoAwesome as AutoAwesomeIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { useNavigation } from '../contexts/NavigationContext';
import { useCustomTheme } from '../contexts/ThemeContext';

const Navigation = ({ activeTab, setActiveTab, setChaptersPage, setVersesPage, setPage }) => {
  const { t } = useLanguage();
  const { mode, toggleTheme } = useCustomTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Derive chapter/verse from URL so nav links are always fresh
  const urlMatch = location.pathname.match(/\/chapters\/(\d+)(?:\/verses(?:\/(\d+))?)?/);
  const urlChapter = urlMatch ? urlMatch[1] : null;
  const urlVerse = urlMatch ? urlMatch[2] : null;
  const { currentChapter: ctxChapter, currentVerse: ctxVerse } = useNavigation();
  const currentChapter = urlChapter || ctxChapter;
  const currentVerse = urlVerse || ctxVerse;

  const resetListPages = () => {
    setChaptersPage?.(1);
    setVersesPage?.(1);
    setPage?.(1); // legacy prop compatibility
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (newValue) => {
    setAnchorEl(null);
    if (typeof newValue === 'number') {
      handleNavigation(newValue);
    }
  };

  const handleNavigation = (newValue) => {
    setActiveTab(newValue);
    switch (newValue) {
      case 0:
        resetListPages();
        navigate('/');
        break;
      case 1:
        setChaptersPage?.(1);
        setPage?.(1);
        navigate('/chapters');
        break;
      case 2:
        if (currentChapter) {
          navigate(`/chapters/${currentChapter}`);
        } else {
          navigate('/chapters');
        }
        break;
      case 3:
        if (currentChapter) {
          setVersesPage?.(1);
          navigate(`/chapters/${currentChapter}/verses`);
        } else {
          navigate('/chapters');
        }
        break;
      case 4:
        if (currentChapter && currentVerse) {
          navigate(`/chapters/${currentChapter}/verses/${currentVerse}`);
        } else {
          navigate('/chapters');
        }
        break;
      case 5:
        resetListPages();
        navigate('/situations');
        break;
      case 6:
        resetListPages();
        navigate('/bookmarks');
        break;
      case 7:
        resetListPages();
        navigate('/about');
        break;
      default:
        navigate('/');
    }
  };

  const menuItems = [
    { icon: <HomeIcon />, label: t('menu.home'), value: 0 },
    { icon: <MenuBookIcon />, label: t('menu.chapters'), value: 1 },
    { icon: <BookmarkIcon />, label: t('menu.chapterDetails'), value: 2 },
    { icon: <FormatQuoteIcon />, label: t('menu.verses'), value: 3 },
    { icon: <FormatQuoteIcon />, label: t('menu.verseDetails'), value: 4 },
    { icon: <AutoAwesomeIcon />, label: t('menu.lifeWisdom') || 'Life Wisdom', value: 5 },
    { icon: <BookmarkIcon />, label: t('menu.savedVerses') || 'Saved Verses', value: 6 },
    { icon: <InfoIcon />, label: t('menu.about'), value: 7 }
  ];

  const LogoSection = () => (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				cursor: "pointer",
				flexDirection: "row",
			}}
			onClick={() => handleNavigation(0)}>
			<img
				src="/icons/logo.svg"
				alt="Bhagwat Geeta"
				style={{
					width: "36px",
					height: "36px",
					borderRadius: "6px",
					objectFit: "contain"
				}}
			/>
			<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
				<Typography variant="h6" color="primary" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
					Bhagwat Geeta
				</Typography>
				<Typography
					component="a"
					href="https://blivix.com"
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
					variant="caption"
					color="text.secondary"
					sx={{
						display: { xs: "none", sm: "block" },
						fontSize: '0.72rem',
						textDecoration: 'none',
						'&:hover': { color: 'primary.main', textDecoration: 'underline' },
					}}
				>
					by Blivix
				</Typography>
			</Box>
		</Box>
	)

  return (
    <AppBar position="sticky" color="inherit" elevation={1}>
      {/* Mobile Navigation */}
      <Toolbar sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'space-between' }}>
        <LogoSection />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={toggleTheme} color="primary" title="Toggle Light/Dark Theme">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <LanguageSelector variant="icon" />
          <IconButton
            edge="end"
            color="primary"
            onClick={handleMenuClick}
          >
            <MenuIcon />
          </IconButton>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => handleMenuClose()}
          PaperProps={{
            elevation: 3,
            sx: { width: '200px', maxWidth: '100%' }
          }}
        >
          {menuItems.map((item) => (
            <MenuItem 
              key={item.value}
              onClick={() => handleMenuClose(item.value)}
              selected={activeTab === item.value}
              sx={{
                gap: 2,
                py: 1.5
              }}
            >
              {item.icon}
              <Typography variant="body1">{item.label}</Typography>
            </MenuItem>
          ))}
          <LanguageSelector variant="menu" />
        </Menu>
      </Toolbar>

      {/* Desktop Navigation */}
      <Box 
        sx={{ 
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          bgcolor: 'background.paper'
        }}
      >
        <LogoSection />
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => handleNavigation(newValue)}
          variant="standard"
          sx={{
            '& .MuiTabs-flexContainer': {
              justifyContent: 'center',
            },
            '& .MuiTab-root': {
              minWidth: '110px',
              '&.Mui-selected': {
                color: 'primary.main',
              }
            }
          }}
        >
          {menuItems.map((item) => (
            <Tab 
              key={item.value}
              icon={item.icon}
              label={item.label}
              iconPosition="start"
              sx={{
                minWidth: '110px',
                '&.Mui-selected': {
                  color: 'primary.main',
                }
              }}
            />
          ))}
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton onClick={toggleTheme} color="primary" title="Toggle Light/Dark Theme">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <LanguageSelector variant="icon" />
        </Box>
      </Box>
    </AppBar>
  );
};

export default Navigation;
